import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { CatCrest } from "./CatCrest";
import { hours } from "../data/siteConfig";

const LINKS = [
    { label: "Our Story", href: "#story", testid: "nav-story-link" },
    { label: "The Cattery", href: "#cattery", testid: "nav-cattery-link" },
    { label: "Care", href: "#care", testid: "nav-care-link" },
    { label: "Prices & Hours", href: "#pricing", testid: "nav-pricing-link" },
    { label: "Gallery", href: "#gallery", testid: "nav-gallery-link" },
    { label: "Reviews", href: "#reviews", testid: "nav-reviews-link" },
];

const MOBILE_EXTRA = [
    { label: "Contact / Book", href: "#book", testid: "nav-mobile-book-link" },
];

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            <header
                className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
                    scrolled || open
                        ? "border-b border-stone-200 bg-cream/85 backdrop-blur-xl"
                        : "border-b border-transparent bg-transparent"
                }`}
                data-testid="site-nav"
            >
                <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
                    <a
                        href="#top"
                        className="flex items-center gap-3 text-[#1c1917]"
                        data-testid="nav-logo-link"
                        onClick={() => setOpen(false)}
                    >
                        <CatCrest className="h-10 w-10 text-[#1c1917]" />
                        <span className="leading-tight">
                            <span className="block font-serif text-lg font-semibold tracking-wide">
                                Mākara
                            </span>
                            <span className="block font-mono text-[9px] uppercase tracking-[0.24em] text-stone-500">
                                Village Boarding Cattery
                            </span>
                        </span>
                    </a>

                    <nav
                        className="hidden items-center gap-7 lg:flex"
                        aria-label="Primary"
                    >
                        {LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                data-testid={l.testid}
                                className="text-sm font-medium text-stone-600 transition-colors duration-300 hover:text-[#1c1917]"
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href="#book"
                            data-testid="nav-book-button"
                            className="rounded-full bg-[#1c1917] px-5 py-2.5 text-sm font-semibold text-cream shadow-lg shadow-black/10 transition-[background-color,transform] duration-300 hover:bg-black hover:-translate-y-0.5"
                        >
                            Book / Enquire
                        </a>
                    </nav>

                    <button
                        className="rounded-full p-2.5 text-[#1c1917] lg:hidden"
                        onClick={() => setOpen(!open)}
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        data-testid="nav-mobile-menu-button"
                    >
                        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        className="fixed inset-0 z-40 flex flex-col justify-center bg-cream px-8 lg:hidden"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        data-testid="nav-mobile-menu"
                    >
                        <nav className="flex flex-col gap-2" aria-label="Mobile">
                            {[...LINKS, ...MOBILE_EXTRA].map(
                                (l, i) => (
                                    <motion.a
                                        key={l.href}
                                        href={l.href}
                                        data-testid={l.testid}
                                        onClick={() => setOpen(false)}
                                        className="border-b border-stone-200 py-4 font-serif text-3xl text-[#1c1917] transition-colors duration-300 hover:text-fern"
                                        initial={{ opacity: 0, x: -24 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{
                                            delay: 0.08 + i * 0.06,
                                            duration: 0.5,
                                            ease: [0.22, 1, 0.36, 1],
                                        }}
                                    >
                                        {l.label}
                                    </motion.a>
                                ),
                            )}
                        </nav>
                        <motion.p
                            className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-stone-500"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                        >
                            Open {hours.days} · {hours.morning} · {hours.afternoon}
                        </motion.p>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
